import React, { useEffect, useRef, useState } from 'react';
import styles from './ComparisonResult.module.scss';
import {
  ComparisonReportResponse,
  IComparisonResult
} from './ComparisonResult.interface';
import { getBusinessArea, useAppSelector } from 'app/redux';
import { LoadingSpinner } from 'app/components';
import { getComparisonReport, getComparisonStatusStream } from 'integration';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as DiagramIcon } from 'app/assets/Icons/Chips/Diagram.svg';
import { ReactComponent as PackageIcon } from 'app/assets/Icons/Chips/Package.svg';
import { ReactComponent as TargetIcon } from 'app/assets/Icons/Chips/Target.svg';
import { displayToastError } from 'app/utils';
import classnames from 'classnames';
import { buildDownloadHtml, useAnnotations } from './useAnnotations';
import { AnnotationPopover } from './AnnotationPopover';
import { AnnotationSidebar } from './AnnotationSidebar';

const processHtmlContent = (html: string): string => {
  const script = `<script>
document.addEventListener('click', function(e) {
  var link = e.target.closest('a[href^="#"]');
  if (link) {
    e.preventDefault();
    var target = document.querySelector(link.getAttribute('href'));
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  }
});
</script>`;
  return html.includes('</body>')
    ? html.replace('</body>', script + '</body>')
    : html + script;
};

export const ComparisonResult: React.FC<IComparisonResult> = ({
  comparisonId,
  isProcessing = false,
  streamReader,
  onComparisonFailure
}) => {
  const currentService = 'document-compare';
  const currentBusinessArea =
    useAppSelector(getBusinessArea) ?? 'cross-functional';

  type TabType = 'combined' | 'outline' | 'sideBySide';
  const TAB_STORAGE_KEY = 'docCompare.defaultTab';
  const storedTab = localStorage.getItem(TAB_STORAGE_KEY) as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(storedTab ?? 'combined');

  const handleTabChange = (tab: TabType): void => {
    setActiveTab(tab);
    localStorage.setItem(TAB_STORAGE_KEY, tab);
  };
  const [comparisonReport, setComparisonReport] =
    useState<ComparisonReportResponse | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [progress, setProgress] = useState<number | null>(null);
  const [displayProgress, setDisplayProgress] = useState<number>(0);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const {
    comments,
    pendingSelection,
    newCommentText,
    setNewCommentText,
    editingComment,
    setEditingComment,
    sidebarOpen,
    setSidebarOpen,
    highlightedCommentId,
    onIframeLoad,
    cancelSelection,
    confirmAnnotation,
    updateAnnotation,
    deleteAnnotation,
    scrollToAnnotation
  } = useAnnotations(
    comparisonId,
    currentBusinessArea,
    currentService,
    iframeRef,
    activeTab
  );

  useEffect(() => {
    if (progress === null) return;
    const target = progress;
    const interval = setInterval(() => {
      setDisplayProgress((prev) => {
        if (prev >= target) {
          clearInterval(interval);
          return prev;
        }
        return Math.min(prev + 1, target);
      });
    }, 30);
    return () => clearInterval(interval);
  }, [progress]);

  const getProgressLabel = (pct: number): string => {
    if (pct < 5) return 'Setting up comparison...';
    if (pct < 10) return 'Reading your documents...';
    if (pct < 75) return 'Comparing documents...';
    if (pct < 80) return 'Generating AI summary...';
    return 'Preparing your report...';
  };

  useEffect(() => {
    const initializeComparison = (): void => {
      if (isProcessing) {
        handleProcessingComparison();
      } else {
        fetchComparisonReport();
      }
    };

    initializeComparison();
  }, []);

  const handleProcessingComparison = (): void => {
    setIsLoading(true);
    setProgress(0);

    const processStream = async (): Promise<void> => {
      try {
        const reader =
          streamReader ??
          (await getComparisonStatusStream(
            currentBusinessArea,
            currentService,
            comparisonId
          ));

        const decoder = new TextDecoder();
        let done = false;

        while (!done) {
          const result = await reader.read();
          done = result.done;
          if (result.done) break;

          const chunk = decoder.decode(result.value, { stream: true });
          const events = chunk.split('\n\n').filter(Boolean);

          for (let i = 0; i < events.length; i++) {
            const event = events[i];
            const dataLine = event
              .split('\n')
              .find((line) => line.startsWith('data:'));
            if (!dataLine) continue;

            let parsed: { status: string };
            try {
              parsed = JSON.parse(dataLine.replace(/^data:\s*/, ''));
            } catch {
              continue;
            }

            const match = parsed.status.match(/(\d+)%/);
            if (match) {
              setProgress(parseInt(match[1], 10));
            }

            if (parsed.status === '100%') {
              reader.cancel();
              fetchComparisonReport();
              return;
            } else if (parsed.status === '0%') {
              setProgress(null);
              setIsLoading(false);
              reader.cancel();
              displayToastError('Comparison failed, please try again.');
              onComparisonFailure();
              return;
            }
          }
        }
      } catch (error) {
        console.error('SSE Error:', error);
        setIsLoading(false);
      }
    };

    processStream();
  };

  const fetchComparisonReport = (): void => {
    setIsLoading(true);
    setProgress(100);
    getComparisonReport(currentBusinessArea, currentService, comparisonId)
      .then((response) => {
        setComparisonReport(response);
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setProgress(null);
        setIsLoading(false);
      });
  };

  const renderTabContent = (): JSX.Element | null => {
    // resultFile1 = combined, resultFile2 = withcallouts (outline), resultFile3 = sidebyside
    switch (activeTab) {
      case 'combined':
        return comparisonReport!.resultFile1 ? (
          <iframe
            key="combined"
            ref={iframeRef}
            title="Combined View"
            srcDoc={processHtmlContent(comparisonReport!.resultFile1)}
            className={styles.iframe}
            sandbox="allow-scripts allow-same-origin"
            onLoad={onIframeLoad}
          />
        ) : null;
      case 'outline':
        return comparisonReport!.resultFile2 ? (
          <iframe
            key="outline"
            ref={iframeRef}
            title="Outline View"
            srcDoc={processHtmlContent(comparisonReport!.resultFile2)}
            className={styles.iframe}
            sandbox="allow-scripts allow-same-origin"
            onLoad={onIframeLoad}
          />
        ) : null;
      case 'sideBySide':
        return comparisonReport!.resultFile3 ? (
          <iframe
            key="sideBySide"
            ref={iframeRef}
            title="Side by Side View"
            srcDoc={processHtmlContent(comparisonReport!.resultFile3)}
            className={styles.iframe}
            sandbox="allow-scripts allow-same-origin"
            onLoad={onIframeLoad}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className={styles['comparison-result']}>
      <h2 className={styles['report-name']}>{comparisonReport?.name}</h2>

      <div className={styles['tabs-row']}>
        <div className={styles.tabs}>
          <Button
            aria-label="Combined View"
            variant="tertiary"
            size="small"
            onClick={() => handleTabChange('combined')}
            className={classnames({
              [styles.active]: activeTab === 'combined'
            })}
          >
            <Button.Icon>
              <PackageIcon />
            </Button.Icon>
            <Button.Label>Combined View</Button.Label>
          </Button>

          <Button
            aria-label="Combined with Outline"
            variant="tertiary"
            size="small"
            onClick={() => handleTabChange('outline')}
            className={classnames({
              [styles.active]: activeTab === 'outline'
            })}
          >
            <Button.Icon>
              <DiagramIcon />
            </Button.Icon>
            <Button.Label>Combined with Outline</Button.Label>
          </Button>

          <Button
            aria-label="Side by Side"
            variant="tertiary"
            size="small"
            onClick={() => handleTabChange('sideBySide')}
            className={classnames({
              [styles.active]: activeTab === 'sideBySide'
            })}
          >
            <Button.Icon>
              <TargetIcon />
            </Button.Icon>
            <Button.Label>Side by Side</Button.Label>
          </Button>
        </div>

        {comparisonReport && (
          <div className={styles['toolbar-actions']}>
            <button
              className={styles['toolbar-btn']}
              onClick={() => {
                const files = {
                  combined: comparisonReport.resultFile1,
                  outline: comparisonReport.resultFile2,
                  sideBySide: comparisonReport.resultFile3
                };
                const suffixes = {
                  combined: 'combined',
                  outline: 'outline',
                  sideBySide: 'side-by-side'
                };
                const content = files[activeTab];
                if (!content) return;
                const annotatedHtml = buildDownloadHtml(
                  content,
                  comments,
                  activeTab
                );
                const blob = new Blob([annotatedHtml], { type: 'text/html' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${comparisonReport.name ?? 'report'}_${
                  suffixes[activeTab]
                }.html`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              title="Download current view as HTML with annotations"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ verticalAlign: 'middle', marginRight: '0.3rem' }}
                aria-hidden="true"
              >
                <path
                  d="M8 1v9M8 10l-3-3M8 10l3-3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 12h12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              Download Report
            </button>
            <button
              className={classnames(styles['toolbar-btn'], {
                [styles['toolbar-btn--active']]: sidebarOpen
              })}
              onClick={() => setSidebarOpen((v) => !v)}
            >
              Annotations{comments.length > 0 ? ` (${comments.length})` : ''}
            </button>
          </div>
        )}
      </div>

      <div
        className={classnames(styles['report-area'], {
          [styles['report-area--with-sidebar']]: sidebarOpen
        })}
      >
        <div className={styles['tab-content']}>
          {comparisonReport && renderTabContent()}
        </div>

        {sidebarOpen && comparisonReport && (
          <AnnotationSidebar
            comments={comments}
            editingComment={editingComment}
            highlightedCommentId={highlightedCommentId}
            activeTab={activeTab}
            onSetEditing={setEditingComment}
            onUpdate={updateAnnotation}
            onDelete={deleteAnnotation}
            onScrollTo={scrollToAnnotation}
            onClose={() => setSidebarOpen(false)}
          />
        )}
      </div>

      {pendingSelection && (
        <AnnotationPopover
          position={pendingSelection.position}
          value={newCommentText}
          onChange={setNewCommentText}
          onConfirm={confirmAnnotation}
          onCancel={cancelSelection}
        />
      )}

      {isLoading && (
        <div className={styles['loading-overlay']}>
          <LoadingSpinner
            containerStyle={{
              position: 'relative',
              width: 'auto',
              height: 'auto',
              backdropFilter: 'unset'
            }}
          />
          {progress !== null && (
            <div className={styles['progress-container']}>
              <div className={styles['progress-track']}>
                <div
                  className={styles['progress-bar']}
                  style={{ width: `${displayProgress}%` }}
                />
              </div>
              <div className={styles['progress-info']}>
                <span className={styles['progress-label']}>
                  {getProgressLabel(displayProgress)}
                </span>
                <span className={styles['progress-percent']}>
                  {displayProgress}%
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
